import { useEffect, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Storefront, Tag, Plus, X, FloppyDisk } from "@phosphor-icons/react";

const SUGGESTED = [
  // Manaus / Amazonas — regionais primeiro (relevantes para clientes locais)
  "Bemol",
  "Atacadão Nova Era",
  "Baratos e Prontos",
  "DB Supermercados",
  "Nova Era Atacado Físico",
  "Aki Supermercados",
  "Carvalho Supermercados",
  // Marketplaces nacionais
  "Mercado Livre",
  "Amazon",
  "Shopee",
  "Magalu",
];

export default function SettingsPage() {
  const { settings, updateSettings } = useSettings();
  const [brandName, setBrandName] = useState("");
  const [channels, setChannels] = useState([]);
  const [newChannel, setNewChannel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBrandName(settings.brand_name || "");
    setChannels(settings.anchored_channels || []);
  }, [settings]);

  const addChannel = (name) => {
    const v = name.trim();
    if (!v) return;
    if (channels.find((c) => c.toLowerCase() === v.toLowerCase())) {
      toast.error("Canal já adicionado");
      return;
    }
    setChannels([...channels, v]);
    setNewChannel("");
  };

  const removeChannel = (name) => setChannels(channels.filter((c) => c !== name));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({ brand_name: brandName.trim() || "Sabor Dubom", anchored_channels: channels });
      toast.success("Configurações salvas. Reflete em todo o sistema.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const notAdded = SUGGESTED.filter(
    (s) => !channels.find((c) => c.toLowerCase() === s.toLowerCase()),
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <header>
        <div className="label-eyebrow mb-2">Configurações</div>
        <h1 className="h-display" style={{ color: "var(--text-primary)" }}>
          Âncoras de Mercado
        </h1>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
          Defina a marca base (que será usada no portfólio) e os canais ancorados que serão monitorados em toda pesquisa de produto.
        </p>
      </header>

      {/* Brand name */}
      <section className="surface p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Tag size={18} weight="duotone" style={{ color: "var(--gold)" }} />
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Marca de referência (base)</h2>
        </div>
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          Esse nome aparece como canal próprio nos produtos do portfólio (ex.: "Sabor Dubom").
        </p>
        <input
          data-testid="settings-brand"
          className="input-base"
          value={brandName}
          onChange={(e) => setBrandName(e.target.value)}
          placeholder="Sabor Dubom"
        />
      </section>

      {/* Anchored channels */}
      <section className="surface p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Storefront size={18} weight="duotone" style={{ color: "var(--gold)" }} />
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Canais ancorados</h2>
        </div>
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          Marketplaces e atacados monitorados em todas as pesquisas. Aparecem nos botões de busca externa e nas ofertas coletadas.
        </p>

        {/* Current list */}
        <div className="flex flex-wrap gap-2" data-testid="channel-list">
          {channels.length === 0 && (
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>Nenhum canal ancorado.</span>
          )}
          {channels.map((c) => (
            <span
              key={c}
              data-testid={`channel-${c}`}
              className="text-xs px-2 py-1 inline-flex items-center gap-1"
              style={{
                background: "rgba(212,175,55,0.08)",
                color: "var(--gold)",
                border: "1px solid rgba(212,175,55,0.25)",
              }}
            >
              {c}
              <button
                onClick={() => removeChannel(c)}
                className="ml-1 opacity-70 hover:opacity-100"
                data-testid={`remove-${c}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>

        {/* Add via input */}
        <div className="flex gap-2">
          <input
            data-testid="new-channel-input"
            className="input-base flex-1"
            placeholder="Novo canal (ex.: Carrefour)…"
            value={newChannel}
            onChange={(e) => setNewChannel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addChannel(newChannel);
              }
            }}
          />
          <button
            onClick={() => addChannel(newChannel)}
            className="btn-ghost px-3 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-1"
            data-testid="add-channel-btn"
          >
            <Plus size={12} weight="bold" /> Adicionar
          </button>
        </div>

        {/* Suggestions */}
        {notAdded.length > 0 && (
          <div>
            <div className="label-eyebrow mb-2">Sugestões (regionais primeiro)</div>
            <div className="flex flex-wrap gap-2">
              {notAdded.map((s) => (
                <button
                  key={s}
                  onClick={() => addChannel(s)}
                  data-testid={`suggest-${s}`}
                  className="text-xs px-2 py-1 transition-colors"
                  style={{
                    background: "transparent",
                    color: "var(--text-secondary)",
                    border: "1px dashed var(--border-strong)",
                  }}
                >
                  + {s}
                </button>
              ))}
            </div>
            <p className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
              Sugestões priorizam o ecossistema do Amazonas (Bemol, Atacadão Nova Era, Baratos e Prontos, DB, Aki, Carvalho) antes dos marketplaces nacionais.
            </p>
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-gold px-5 py-2.5 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2"
          data-testid="settings-save"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Salvando…" : "Salvar alterações"}
        </button>
      </div>
    </div>
  );
}
